<template>
  <div class="redirect-chain">
    <a-steps
      :current="steps.length - 1"
      :status="getStatus"
      direction="vertical"
      size="small"
    >
      <a-step
        v-for="(step, index) in steps"
        :key="index"
        :title="getStatusText(step.status)"
        :description="step.url"
        :status="getStepStatus(step.status, index)"
      >
        <template #icon>
          <loading-outlined v-if="index < steps.length - 1" />
          <check-circle-outlined v-else-if="isSuccess(step.status)" style="color: #52c41a;" />
          <close-circle-outlined v-else-if="isError(step.status)" style="color: #ff4d4f;" />
          <warning-outlined v-else style="color: #faad14;" />
        </template>
      </a-step>
    </a-steps>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons-vue'
import type { RedirectStep } from '../types'

interface Props {
  steps: RedirectStep[]
}

const props = defineProps<Props>()

const getStatus = computed(() => {
  const lastStep = props.steps[props.steps.length - 1]
  if (!lastStep) return 'process'
  
  if (lastStep.status >= 200 && lastStep.status < 300) return 'finish'
  if (lastStep.status >= 400) return 'error'
  return 'process'
})

const getStatusText = (status: number): string => {
  const statusTexts: Record<number, string> = {
    200: '200 OK',
    201: '201 Created',
    204: '204 No Content',
    301: '301 Moved Permanently',
    302: '302 Found',
    303: '303 See Other',
    304: '304 Not Modified',
    307: '307 Temporary Redirect',
    308: '308 Permanent Redirect',
    400: '400 Bad Request',
    401: '401 Unauthorized',
    403: '403 Forbidden',
    404: '404 Not Found',
    500: '500 Internal Server Error',
    502: '502 Bad Gateway',
    503: '503 Service Unavailable',
    504: '504 Gateway Timeout'
  }
  
  return statusTexts[status] || `${status}`
}

const getStepStatus = (status: number, index: number): string => {
  if (index < props.steps.length - 1) return 'finish'
  
  if (status >= 200 && status < 300) return 'finish'
  if (status >= 400) return 'error'
  return 'process'
}

const isSuccess = (status: number): boolean => {
  return status >= 200 && status < 300
}

const isError = (status: number): boolean => {
  return status >= 400
}
</script>

<style scoped lang="scss">
.redirect-chain {
  padding: 16px;
  background: #fafafa;
  border-radius: 4px;
}
</style>
